import {
  useState,
  useRef,
  useEffect,
  MouseEvent as ReactMouseEvent,
} from 'react'
import { Header } from '@tanstack/react-table'

export const useColumnResizing = <TData>(header: Header<TData, unknown>) => {
  const [isResizing, setIsResizing] = useState(false)
  const resizingRef = useRef(false)

  useEffect(() => {
    if (!isResizing) return

    const blockEvents = (e: Event) => {
      e.stopPropagation()
      e.preventDefault()
    }

    const endResize = () => {
      document.removeEventListener('click', blockEvents, true)
      setTimeout(() => {
        setIsResizing(false)
        resizingRef.current = false
      }, 0)
    }

    document.addEventListener('click', blockEvents, true)
    document.addEventListener('mouseup', endResize, { once: true })

    return () => {
      document.removeEventListener('click', blockEvents, true)
      document.removeEventListener('mouseup', endResize)
    }
  }, [isResizing])

  const handleResizeStart = (e: ReactMouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    resizingRef.current = true
    setIsResizing(true)
    header.getResizeHandler()(e)
  }

  const handleHeaderClick = (e: ReactMouseEvent) => {
    if (isResizing || resizingRef.current) {
      e.stopPropagation()
      e.preventDefault()
      return
    }
    header.column.getToggleSortingHandler()?.(e)
  }

  const resetColumnSize = (e: ReactMouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    header.column.resetSize()
  }

  const preventEvent = (e: ReactMouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }

  return {
    isResizing,
    handleResizeStart,
    handleHeaderClick,
    resetColumnSize,
    preventEvent,
  }
}
